import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
}

const variantClasses = {
  primary:
    'bg-nova-primary text-nova-primary-foreground hover:bg-nova-primary/90',
  secondary:
    'bg-nova-secondary text-nova-secondary-foreground hover:bg-nova-secondary/80',
  danger:
    'bg-nova-destructive text-nova-destructive-foreground hover:bg-nova-destructive/90',
  ghost: 'text-nova-foreground hover:bg-nova-accent hover:text-nova-accent-foreground',
};

const sizeClasses = {
  sm: 'h-7 rounded-lg px-2.5 text-xs',
  md: 'h-8 rounded-lg px-2.5 text-sm',
  lg: 'h-9 rounded-lg px-3 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring/40',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <span
            className="h-3.5 w-3.5 animate-spin rounded-lg border-2 border-current border-t-transparent"
            aria-hidden
          />
        ) : null}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
